import { Link } from "react-router-dom";
import Breadcrumb from "@/components/ui/Breadcrumb";
import SectionHeading from "@/components/ui/Section";
import { ContactForm, SubscribeForm } from "@/components/ui/forms";
import { useJsonLd, usePageMeta } from "@/lib/site";

const PRINCIPLES = [
  {
    icon: "ri-lock-2-line",
    title: "匿名保护",
    text: "所有圈内信源默认匿名呈现，不公开任何可定位到具体个人的身份信息，让敢开口的人安心开口。",
  },
  {
    icon: "ri-search-eye-line",
    title: "多源印证",
    text: "爆料类内容以「至少在不同场景被多人独立提及」为基本门槛，绝不单凭一面之词上稿。",
  },
  {
    icon: "ri-close-circle-line",
    title: "拒绝造谣",
    text: "我们不做标题党式恶意中伤，也不传播无法印证的人身攻击。宁可少一条，也不发一条靠不住的。",
  },
  {
    icon: "ri-scales-3-line",
    title: "有据可查",
    text: "正文会尽量交代时间、场景与信源身份（如“剧组工作人员”“经纪人”），方便你判断可信度。",
  },
];

const STEPS = [
  { n: "01", title: "信源递交", text: "圈内工作人员与知情者通过投稿通道匿名递交素材与线索。" },
  { n: "02", title: "编辑部核实", text: "编辑部交叉比对、去隐私化，把能公开的行业细节保留下来。" },
  { n: "03", title: "审慎呈现", text: "经信源原则审核后，以虚构化的行业观察笔法整理上线，每日更新。" },
];

const VOICES = [
  { icon: "ri-double-quotes-l", text: "既吃瓜又不失分寸，是我为数不多愿意推荐的圈内内容源。", name: "一只爱熬夜的猹", role: "专栏读者" },
  { icon: "ri-double-quotes-l", text: "写试镜、写档期的角度很有业内味道，看得出有真正懂行的人坐镇。", name: "片场观察员", role: "从业读者" },
  { icon: "ri-double-quotes-l", text: "遇到拿不准的内容会主动标注信源与谨慎程度，这点很难得。", name: "阿梨", role: "每日订阅用户" },
];

export default function About() {
  usePageMeta(
    "关于我们与信源原则 - 明星八卦圈内",
    "了解明星八卦圈内的品牌介绍、网站定位、发展理念与内容原则，以及我们的读者口碑与投稿联系方式。"
  );
  useJsonLd({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        name: "明星八卦圈内",
        url: "https://yslcare.cn/",
        email: "hello@mingxingbaguajuan.example",
        description:
          "汇集剧组工作人员、经纪人、业内制片与知情者一手消息的明星八卦圈内爆料门户。",
      },
      {
        "@type": "AboutPage",
        name: "关于我们 - 明星八卦圈内",
        url: "https://yslcare.cn/about",
        inLanguage: "zh-CN",
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://yslcare.cn/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "https://yslcare.cn/about" },
        ],
      },
    ],
  });

  return (
    <>
      <section className="container-page pt-6 md:pt-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
        {/* 头图 intro */}
        <div className="relative mt-4 overflow-hidden rounded-lg bg-secondary-100">
          <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-accent-200/60"></div>
          <div className="pointer-events-none absolute -bottom-20 left-1/3 h-56 w-56 rounded-full bg-primary-100/70"></div>
          <div className="relative z-10 grid grid-cols-1 gap-6 p-7 md:p-10 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="label-caps text-accent-700">
                <i className="ri-flashlight-fill"></i>
                About Us
              </span>
              <h1 className="mt-3 font-heading text-3xl font-black leading-snug text-foreground-950 md:text-4xl">
                我们想为「明星八卦圈内」做一点干净的记录
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                这是一家只聚焦娱乐圈「圈内真相」的内容站点。我们相信，明星八卦圈内的水很深，但其中真正值得被记录的，
                不是恶意撕扯，而是那些剧组工作人员、经纪人、业内制片与知情者亲历的、有细节的行业侧写。
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { v: "128+", l: "入驻圈内信源", i: "ri-user-heart-line" },
                { v: "486+", l: "累计更新内容", i: "ri-newspaper-line" },
                { v: "3100万+", l: "累计阅读热度", i: "ri-eye-line" },
                { v: "0", l: "恶意造谣转载", i: "ri-shield-check-line" },
              ].map((s) => (
                <div
                  key={s.l}
                  className="rounded-lg bg-background-50 p-4 text-center"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-600">
                    <i className={s.i}></i>
                  </span>
                  <p className="mt-2 font-heading text-2xl font-extrabold text-foreground-950">
                    {s.v}
                  </p>
                  <p className="text-xs text-foreground-500">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 定位 */}
      <section className="container-page mt-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <SectionHeading kicker="Positioning" title="网站定位" />
            <p className="text-sm leading-7 text-foreground-700">
              我们把「八卦」拆成两层来看：一层是热搜上最热闹的流量游戏，另一层是镜头后真实的行业运转。
              明星八卦圈内坚持做后者的观察者——用剧组盒饭、大夜戏时间表、试镜室里的笔记、应酬桌边的真话，
              为你拼出一张更有厚度的娱乐圈地图。
            </p>
            <p className="mt-4 text-sm leading-7 text-foreground-700">
              内容面向所有对幕后、行业与内幕好奇的读者，也欢迎真正的圈内工作人员、经纪人与知情者，
              把那些「可以讲、又不想被点名」的故事，托付给我们稳妥地讲出来。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <h3 className="flex items-center gap-2 font-heading text-lg font-extrabold text-foreground-950">
              <i className="ri-folders-line text-primary-500"></i>
              内容栏目一览
            </h3>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-start gap-3 border-b border-background-200 pb-3">
                <i className="ri-clapperboard-line mt-0.5 text-primary-500"></i>
                <div><p className="font-semibold text-foreground-900">剧组内幕</p><p className="text-foreground-500">现场人员眼里的真实片场</p></div>
              </li>
              <li className="flex items-start gap-3 border-b border-background-200 pb-3">
                <i className="ri-phone-line mt-0.5 text-primary-500"></i>
                <div><p className="font-semibold text-foreground-900">经纪人手记</p><p className="text-foreground-500">台前幕后的人情与规则</p></div>
              </li>
              <li className="flex items-start gap-3 border-b border-background-200 pb-3">
                <i className="ri-line-chart-line mt-0.5 text-primary-500"></i>
                <div><p className="font-semibold text-foreground-900">业内观察</p><p className="text-foreground-500">档期、报价与数据里的冷热</p></div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-lock-2-line mt-0.5 text-primary-500"></i>
                <div><p className="font-semibold text-foreground-900">知情者密档</p><p className="text-foreground-500">那些无法署名的圈内细节</p></div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* 原则 */}
      <section id="principle" className="container-page mt-14 md:mt-20 scroll-mt-24">
        <SectionHeading
          kicker="Content Principles"
          title="我们的内容原则"
          desc="匿名信源是一把双刃剑。我们用一套明确的标准，让它只带来价值，而不是伤害。"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PRINCIPLES.map((p) => (
            <div
              key={p.title}
              className="rounded-lg border border-background-200 bg-background-100 p-6"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-xl text-primary-600">
                <i className={p.icon}></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-extrabold text-foreground-950">
                {p.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 理念 timeline */}
      <section className="container-page mt-14 md:mt-20">
        <SectionHeading
          kicker="How We Work"
          title="一篇爆料是如何出炉的"
          center
        />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <div key={s.n} className="relative rounded-lg bg-background-100 p-6">
              <span className="font-heading text-3xl font-black text-primary-100">{s.n}</span>
              {i < STEPS.length - 1 && (
                <i className="ri-arrow-right-line absolute right-4 top-8 hidden text-lg text-accent-400 md:block"></i>
              )}
              <h3 className="mt-2 font-heading text-lg font-extrabold text-foreground-950">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 口碑 */}
      <section id="voices" className="container-page mt-14 scroll-mt-24 md:mt-20">
        <SectionHeading
          kicker="Word of Mouth"
          title="读者怎么说我们"
          desc="这些留言来自真实订阅与追更用户，也来自愿意以匿名身份交流的从业者。"
        />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {VOICES.map((v) => (
            <figure key={v.name} className="rounded-lg bg-accent-100 p-6">
              <i className={`${v.icon} text-2xl text-accent-500`}></i>
              <blockquote className="mt-3 text-sm leading-relaxed text-foreground-800">
                {v.text}
              </blockquote>
              <figcaption className="mt-4 text-xs text-foreground-600">
                <span className="font-semibold text-foreground-900">{v.name}</span>
                <span className="text-foreground-400"> · {v.role}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* 订阅 */}
      <section className="container-page mt-14 md:mt-20">
        <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <div className="grid grid-cols-1 items-center gap-6 md:grid-cols-2">
            <div>
              <h2 className="font-heading text-xl font-extrabold text-foreground-950">
                成为第一批看到热瓜的人
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                订阅每日瓜讯，编辑部筛选过的重点圈内内容，每天一封轻量邮件送达。
              </p>
            </div>
            <SubscribeForm />
          </div>
        </div>
      </section>

      {/* 联系 */}
      <section id="contact" className="container-page mt-14 scroll-mt-24 md:mt-20">
        <div className="overflow-hidden rounded-lg bg-secondary-100">
          <div className="grid grid-cols-1 gap-8 p-7 md:p-10 lg:grid-cols-2">
            <div>
              <SectionHeading kicker="Get in Touch" title="投稿 & 联系我们" />
              <p className="text-sm leading-7 text-foreground-700">
                你是剧组工作人员、经纪人、业内制片，或者只是一名手握线索的知情者？
                欢迎通过右侧表单与我们匿名沟通。我们承诺：仅用于编辑部核实，绝不公开你的任何身份信息。
              </p>
              <div className="mt-5 space-y-3 text-sm text-foreground-700">
                <a
                  href="mailto:hello@mingxingbaguajuan.example"
                  className="flex items-center gap-3 transition-colors hover:text-primary-600"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50 text-primary-600">
                    <i className="ri-mail-line"></i>
                  </span>
                  hello@mingxingbaguajuan.example
                </a>
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50 text-primary-600">
                    <i className="ri-time-line"></i>
                  </span>
                  工作日 10:00 - 19:00 回复
                </div>
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50 text-primary-600">
                    <i className="ri-shield-keyhole-line"></i>
                  </span>
                  匿名信源保护承诺
                </div>
              </div>
            </div>
            <div className="rounded-lg bg-background-50 p-6 md:p-7">
              <ContactForm />
            </div>
          </div>
        </div>
        <p className="mt-4 text-center text-xs text-foreground-400">
          <Link to="/" className="transition-colors hover:text-primary-600">
            返回首页
          </Link>
          &nbsp;·&nbsp;去看看剧组内幕
        </p>
      </section>
    </>
  );
}